import { BaseError } from './BaseError';

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

/**
 * General application error for non-specific errors
 */
export class ApplicationError extends BaseError {
  constructor(
    message: string,
    public readonly severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    options: {
      isOperational?: boolean;
      cause?: Error;
      context?: Record<string, unknown>;
    } = {}
  ) {
    super(message, {
      name: 'ApplicationError',
      isOperational: options.isOperational !== undefined ? options.isOperational : true,
      cause: options.cause,
      context: {
        severity,
        ...options.context,
      },
    });
  }

  /**
   * Create a low severity application error
   */
  public static lowSeverity(
    message: string,
    options?: {
      isOperational?: boolean;
      cause?: Error;
      context?: Record<string, unknown>;
    }
  ): ApplicationError {
    return new ApplicationError(message, ErrorSeverity.LOW, options);
  }

  /**
   * Create a medium severity application error
   */
  public static mediumSeverity(
    message: string,
    options?: {
      isOperational?: boolean;
      cause?: Error;
      context?: Record<string, unknown>;
    }
  ): ApplicationError {
    return new ApplicationError(message, ErrorSeverity.MEDIUM, options);
  }

  /**
   * Create a high severity application error
   */
  public static highSeverity(
    message: string,
    options?: {
      isOperational?: boolean;
      cause?: Error;
      context?: Record<string, unknown>;
    }
  ): ApplicationError {
    return new ApplicationError(message, ErrorSeverity.HIGH, options);
  }

  /**
   * Create a critical severity application error
   */
  public static criticalSeverity(
    message: string,
    options?: {
      isOperational?: boolean;
      cause?: Error;
      context?: Record<string, unknown>;
    }
  ): ApplicationError {
    return new ApplicationError(message, ErrorSeverity.CRITICAL, options);
  }

  /**
   * Standardized JSON representation including severity
   */
  public override toJSON(): Record<string, unknown> {
    const baseJson = super.toJSON();
    return {
      ...baseJson,
      severity: this.severity,
    };
  }
}
